-- | Step 3 reducer-style event handling.
-- |
-- | This module owns save/load/jump policy. It emits commands for the app
-- | runtime instead of starting effects or writing browser history directly.
module Steps.Step3.Update
    ( Msg(..)
    , Command(..)
    , update
    , applyStartedCommand
    ) where

import Common

import API.Autofill (AutofillArgs, AutofillResult, initAutofillArgs)
import API.Document (Document, ToggleLockResult)
import App.Operation.Types (OperationId)
import App.Route as AppRoute
import Data.Array as Arr
import Data.Maybe (Maybe(..), isJust, isNothing)
import Data.Tuple (Tuple(..))
import Error (ErrorWrapper)
import App.Step (Navigation(..))
import Steps.Step3.Types as Step3Types
import Steps.Step3.State (PostSaveAction(..), Step3State, applyAutofillResult, applyLoadedEditorState, applySavedDocumentState, applyToggleLockResult, isBusy, mapEditorState, setPendingLoadTarget, shouldSaveCurrentDocument)
import Steps.Step3.State as Step3State
import Steps.Step3.Transition (PendingTransition, Step3HistoryMode, enqueueTransition, finishTransition, latestTransition, transitionIsCurrent)
import Steps.WorkQueue.DocumentIndex (DocumentLookup)
import Steps.WorkQueue.State (FormSectionID)
import Steps.WorkQueue.View (WorkQueueResult(..))
import Widgets.Form.Class (FormWidgetAction(..))

data Msg
    = ViewEvent Step3Types.FormFillResult
    | ConfirmCancelResult Boolean
    | SaveCompleted OperationId (Either ErrorWrapper Document)
    | LoadCompleted OperationId Int (Either ErrorWrapper Step3Types.FillQueueState)
    | AutofillCompleted OperationId (Either ErrorWrapper AutofillResult)
    | LockCompleted OperationId (Either ErrorWrapper ToggleLockResult)
    | DocumentMutationCompleted OperationId (Either ErrorWrapper Step3Types.DocumentMutationResult)

data Command
    = SyncRoute Step3HistoryMode
    | StartSave (Maybe Int)
    | StartLoad PendingTransition
    | StartAutofill AutofillArgs
    | StartToggleLock
    | StartDocumentMutation Step3Types.DocumentMutationRequest
    | ConfirmCancel
    | AllowPageUnload
    | BlockPageUnload
    | OpenExportUrl String
    | Navigate Navigation
    | MarkSuperseded OperationId

type UpdateResult =
    { state :: Step3State
    , commands :: Array Command
    }

update :: Step3State -> Msg -> Either ErrorWrapper UpdateResult
update state = case _ of
    ConfirmCancelResult confirmed ->
        pure
            { state
            , commands: if confirmed then [ Navigate NavCancel ] else []
            }

    SaveCompleted opId _
        | state.saveOperationId /= Just opId ->
            pure { state, commands: [] }

    SaveCompleted opId result -> do
        nextState <- case result of
            Left err -> Left err
            Right doc -> (flip applySavedDocumentState) doc $
                state
                    { saveOperationId = clearIfMatches opId state.saveOperationId
                    }
        let afterSaveState = nextState { pendingPostSave = Nothing }
        let baseCommands = [ AllowPageUnload ]
        case latestTransition afterSaveState.editor.transitionState of
            Just target ->
                continuePendingTransition afterSaveState target baseCommands
            Nothing ->
                pure
                    { state: afterSaveState
                    , commands: baseCommands <> case nextState.pendingPostSave of
                        Nothing -> []
                        Just SaveThenCancel -> [ Navigate NavCancel ]
                        Just SaveThenFinish -> [ Navigate NavNext ]
                        Just (SaveThenExport report) -> [ OpenExportUrl report.url ]
                        Just SaveThenLock -> [ StartToggleLock ]
                    }

    LoadCompleted opId _ _
        | state.loadOperationId /= Just opId ->
            pure { state, commands: [] }

    LoadCompleted opId generation result ->
        if transitionIsCurrent state.editor.transitionState generation
            then case result of
                Left err -> Left err
                Right loadedEditor -> do
                    let loadedState = applyLoadedEditorState (clearLoadOperation opId state) loadedEditor
                    case latestTransition loadedState.editor.transitionState of
                        Just target -> do
                            let Tuple finished transitionState = finishTransition loadedState.editor.transitionState target.generation
                            let settledState = loadedState
                                    { editor = loadedState.editor
                                        { transitionState = transitionState
                                        , anchor = Nothing
                                        }
                                    }
                            if finished
                                then pure
                                    { state: settledState
                                    , commands: [ SyncRoute target.historyMode ]
                                    }
                                else beginPendingTransition settledState
                        Nothing ->
                            pure { state: loadedState, commands: [] }
            else do
                next <- beginPendingTransition $ clearLoadOperation opId state
                pure next { commands = [ MarkSuperseded opId ] <> next.commands }

    AutofillCompleted opId _
        | state.autofillOperationId /= Just opId ->
            pure { state, commands: [] }

    AutofillCompleted opId result -> do
        nextState <- case result of
            Left err -> Left err
            Right res -> applyAutofillResult state res
        pure { state: nextState { autofillOperationId = clearIfMatches opId nextState.autofillOperationId }, commands: [] }

    LockCompleted opId _
        | state.lockOperationId /= Just opId ->
            pure { state, commands: [] }

    LockCompleted opId result ->
        case result of
            Left err -> Left err
            Right lockResult ->
                pure
                    { state: (flip applyToggleLockResult) lockResult
                        state { lockOperationId = clearIfMatches opId state.lockOperationId }
                    , commands: [ ]
                    }

    DocumentMutationCompleted opId _
        | state.documentMutationOperationId /= Just opId ->
            pure { state, commands: [] }

    DocumentMutationCompleted opId result -> do
        nextState <- case result of
            Left err -> Left err
            Right mutationResult -> Step3State.applyDocumentMutationResult state mutationResult
        let clearedState = nextState
                { documentMutationOperationId =
                    clearIfMatches opId nextState.documentMutationOperationId
                }
        case result of
            Right (Step3Types.SignatureSigned _) ->
                pure
                    { state: clearedState
                    , commands: [ StartDocumentMutation $ Step3Types.RefreshSignatureGroup clearedState.editor.fillState.doc.id ]
                    }
            _ ->
                beginPendingTransition clearedState

    ViewEvent event
        | isJust $ oneOf [ state.saveOperationId, state.loadOperationId, state.documentMutationOperationId ] ->
            pure { state: captureTransitionDuringBusy state event, commands: [] }

    ViewEvent _
        | isBusy state ->
            pure { state, commands: [] }

    ViewEvent (Step3Types.WorkQF (UIStateChange newUi)) ->
        pure
            { state: state
                { editor = state.editor
                    { workQueueUi = newUi }
                }
            , commands: []
            }

    ViewEvent (Step3Types.WorkQueueMetadataF workQueueState workQueueUi) ->
        pure
            { state: state
                { editor = state.editor
                    { workQueueState = workQueueState
                    , workQueueUi = workQueueUi
                    }
                }
            , commands: [ BlockPageUnload ]
            }

    ViewEvent (Step3Types.WorkQF (JumpToForm navigation _)) ->
        requestTransition state $
            lookupTransitionTarget navigation.lookup navigation.section state

    ViewEvent (Step3Types.StateF fstate) ->
        pure
            { state: state
                { editor = state.editor
                    { fillState = fstate }
                }
            , commands: [ BlockPageUnload ]
            }

    ViewEvent (Step3Types.JumpF lookup section) ->
        requestTransition state $ lookupTransitionTarget lookup section state

    ViewEvent (Step3Types.JumpIdxF idx _)
        | idx < 0 || idx > length state.editor.queue - 1 ->
            pure { state, commands: [] }

    ViewEvent (Step3Types.JumpIdxF idx section) ->
        requestTransition state $ Just { idx, section }

    ViewEvent Step3Types.NextF
        | Step3Types.onLastItem state.editor ->
            if shouldSaveCurrentDocument state
                then pure
                    { state: state { pendingPostSave = Just SaveThenFinish }
                    , commands: [ StartSave Nothing ]
                    }
                else pure
                    { state
                    , commands: [ Navigate NavNext ]
                    }

    ViewEvent Step3Types.NextF ->
        requestTransition state $ Just { idx: state.editor.position + 1, section: Nothing }

    ViewEvent Step3Types.PrevF
        | state.editor.position <= 0 ->
            requestTransition state $ Just { idx: length state.editor.queue - 1, section: Nothing }

    ViewEvent Step3Types.PrevF ->
        requestTransition state $ Just { idx: state.editor.position - 1, section: Nothing }

    ViewEvent Step3Types.SaveF ->
        pure
            { state
            , commands: if shouldSaveCurrentDocument state then [ StartSave Nothing ] else []
            }

    ViewEvent Step3Types.CancelF ->
        pure { state, commands: [ ConfirmCancel ] }

    ViewEvent Step3Types.SaveCancelF ->
        if shouldSaveCurrentDocument state
            then pure
                { state: state { pendingPostSave = Just SaveThenCancel }
                , commands: [ StartSave Nothing ]
                }
            else pure
                { state
                , commands: [ Navigate NavCancel ]
                }

    ViewEvent (Step3Types.ExportF report) ->
        if shouldSaveCurrentDocument state
            then pure
                { state: state { pendingPostSave = Just (SaveThenExport report) }
                , commands: [ StartSave Nothing ]
                }
            else pure
                { state
                , commands: [ OpenExportUrl report.url ]
                }

    ViewEvent Step3Types.AutofillF ->
        pure
            { state
            , commands:
                [ StartAutofill $ initAutofillArgs state.editor.fillState.doc.formId state.editor.fillState.doc.student.id ]
            }

    ViewEvent Step3Types.ToggleLockF ->
        -- Locking removes normal edit access, so an unlocked dirty form must be
        -- saved before the lock mutation runs.
        if isNothing state.editor.fillState.doc.locked_at && shouldSaveCurrentDocument state
            then pure
                { state: state { pendingPostSave = Just SaveThenLock }
                , commands: [ StartSave Nothing ]
                }
            else pure
                { state
                , commands: [ StartToggleLock ]
                }

    ViewEvent (Step3Types.ActionF (FWAutoFill afArgs) fstate) ->
        pure
            { state: state
                { editor = state.editor
                    { fillState = fstate }
                }
            , commands: [ StartAutofill afArgs ]
            }

    ViewEvent (Step3Types.DocumentMutationF fstate request) ->
        pure
            { state: state
                { editor = state.editor
                    { fillState = fstate }
                }
            , commands: [ StartDocumentMutation request ]
            }

    ViewEvent Step3Types.ScrollF ->
        pure
            { state: mapEditorState (\editor -> editor { anchor = Nothing }) state
            , commands: []
            }

requestTransition
    :: Step3State
    -> Maybe { idx :: Int, section :: Maybe FormSectionID }
    -> Either ErrorWrapper UpdateResult
requestTransition state Nothing = pure { state, commands: [] }
requestTransition state (Just { idx, section }) =
    let Tuple shouldStart transitionState = enqueueTransition state.editor.transitionState idx section
        queuedState = mapEditorState (\editor -> editor { transitionState = transitionState }) state
    in if shouldStart
        then beginPendingTransition queuedState
        else pure { state: queuedState, commands: [] }

beginPendingTransition :: Step3State -> Either ErrorWrapper UpdateResult
beginPendingTransition state = case latestTransition state.editor.transitionState of
    Nothing ->
        pure { state, commands: [] }
    Just target ->
        continuePendingTransition state target []

continuePendingTransition :: Step3State -> PendingTransition -> Array Command -> Either ErrorWrapper UpdateResult
continuePendingTransition state target prefixCommands
    | target.position == state.editor.position =
        let nextEditor = state.editor
                { anchor = Nothing
                , activeSection = target.anchor
                , sectionIntent = maybe AppRoute.SectionClear AppRoute.SectionSet target.anchor
                }
            Tuple finished transitionState = finishTransition nextEditor.transitionState target.generation
            settledState = state { editor = nextEditor { transitionState = transitionState } }
            commands = prefixCommands <> [ SyncRoute target.historyMode ]
        in if finished
            then pure { state: settledState, commands }
            else beginPendingTransition settledState
    | isJust state.saveOperationId || isJust state.loadOperationId =
        pure { state, commands: prefixCommands }
    | shouldSaveCurrentDocument state =
        pure { state, commands: prefixCommands <> [ StartSave (Just target.generation) ] }
    | otherwise =
        pure { state, commands: prefixCommands <> [ StartLoad target ] }

captureTransitionDuringBusy :: Step3State -> Step3Types.FormFillResult -> Step3State
captureTransitionDuringBusy state event =
    state { editor = Step3Types.captureTransitionIntent state.editor event }

lookupTransitionTarget
    :: DocumentLookup
    -> Maybe FormSectionID
    -> Step3State
    -> Maybe { idx :: Int, section :: Maybe FormSectionID }
lookupTransitionTarget lookup section state =
    map (\idx -> { idx, section }) $ Arr.findIndex (Step3Types.matchesLookup lookup) state.editor.queue

applyStartedCommand :: Step3State -> Command -> OperationId -> Step3State
applyStartedCommand state command opId = case command of
    StartSave _ -> state { saveOperationId = Just opId }
    StartLoad target -> (setPendingLoadTarget target state) { loadOperationId = Just opId }
    StartAutofill _ -> state { autofillOperationId = Just opId }
    StartToggleLock -> state { lockOperationId = Just opId }
    StartDocumentMutation _ -> state { documentMutationOperationId = Just opId }
    _ -> state

clearIfMatches :: OperationId -> Maybe OperationId -> Maybe OperationId
clearIfMatches opId current =
    if current == Just opId then Nothing else current

clearLoadOperation :: OperationId -> Step3State -> Step3State
clearLoadOperation opId state =
    state { loadOperationId = clearIfMatches opId state.loadOperationId }
